import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { Request } from '../apis';

const featureFlagsSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    isEnabled: z.boolean()
  })
  .strict();

type FeatureFlagsState = {
  featureFlags: FeatureFlagDetails[];
};

type FeatureFlagDetails = z.infer<typeof featureFlagsSchema>;

type FeatureFlagProps = {
  allFeatures: FeatureFlagDetails[];
  enabledFeatures: FeatureFlagDetails[]; // todo type this better
  isFeatureFlagEnabled: (name: string) => boolean | Error | never;
  updateFeatureFlag: (featureFlag: FeatureFlagDetails) => void | Error | never;
};

const defaultFeatureFlagsContext: FeatureFlagProps = {
  allFeatures: [],
  enabledFeatures: [],
  isFeatureFlagEnabled: () =>
    new Error('isFeatureFlagEnabled() not implemented'),
  updateFeatureFlag: () => new Error('updateFeatureFlag() not implemented')
};

const defaultFeatureFlagsState: FeatureFlagsState = {
  featureFlags: []
};

const FeatureFlagsContext = createContext<FeatureFlagProps>(
  defaultFeatureFlagsContext
);

const FeatureFlagsProvider = ({ children }: PropsWithChildren) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsState>(
    defaultFeatureFlagsState
  );

  const enabledFeatureFlags = useMemo(() => {
    return featureFlags.featureFlags
      .filter(({ isEnabled }: FeatureFlagDetails) => isEnabled)
      .map(({ name }: FeatureFlagDetails) => name);
  }, [featureFlags]);

  const isFeatureFlagEnabled = (featureFlagName: string) => {
    // TODO log
    //console.log('isFeatureFlagEnabled', featureFlagName);
    return enabledFeatureFlags.includes(featureFlagName);
  };

  const initFeatureFlags = async () => {
    // TODO log
    //console.log('initFeatureFlags');
    const featureFlags = await Request.get({
      path: 'featureFlags/enabled',
      schema: z.array(featureFlagsSchema)
      // base: process.env.REACT_APP_STUB_URL
    });
    setFeatureFlags({ featureFlags: featureFlags });
  };

  const updateFeatureFlag = (updatedFeatureFlag: FeatureFlagDetails) => {
    // TODO log
    //console.log('updateFeatureFlag', updatedFeatureFlag);
    setFeatureFlags({
      featureFlags: [...featureFlags.featureFlags, updatedFeatureFlag]
    });
  };

  useEffect(() => {
    // TODO log
    //console.log('ffcontext');
    initFeatureFlags();
  }, []);
  const contextValue: FeatureFlagProps = {
    allFeatures: [],
    enabledFeatures: [],
    isFeatureFlagEnabled: isFeatureFlagEnabled,
    updateFeatureFlag: updateFeatureFlag
  };
  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

type FeatureFlagWrapperProps = {
  featureFlagName: string;
};
const FeatureFlagWrapper: React.FC<
  PropsWithChildren<FeatureFlagWrapperProps>
> = ({ children, featureFlagName }) => {
  const { isFeatureFlagEnabled } = useContext(FeatureFlagsContext);

  if (isFeatureFlagEnabled(featureFlagName)) {
    return null;
  }

  return <>{children}</>;
};
export { FeatureFlagsProvider, FeatureFlagWrapper, type FeatureFlagDetails };
